import { handleSystemErrorReport } from '@mb/lib/utils';
import axios from 'axios';

import { getCookies } from './dom.utils';
import { isBannersPage, isHomePage, isSearchPage } from './helpers';
import { ProductSearchMaterial, SortOption, ApiRawMaterial } from '../types';

export const transformSearchspringResponseToAppFormat = (
  result: ApiRawMaterial,
): ProductSearchMaterial => ({
  productFinishType: result.product_finish_type || '',
  paintProductType: result.paint_product_type || '',
  isNew: result.is_new || false,
  isOrderable: result.is_orderable || false,
  isProducts: result.is_products || false,
  isGeoRestricted: result.is_geo_restricted || false,
  isJustAdded: result.just_added || false,
  isCustom: false,
  isCollection: result.is_products || false,
  isInStock: result.in_stock === '1',
  id: result.id,
  childrenCount: result.children_count || '',
  californiaProp65: result.california_prop_65 || [],
  isInRealTimeStock: result.isInRealTimeStock || false,
  entityId: result.entity_id,
  ssStatePermission: result.ss_state_permission || [],
  manufacturer: result.manufacturer || '',
  manufacturerSku: result.manufacturer_sku || [],
  productType: result.product_type || '',
  qty: result.qty || '',
  residentialCommercial: result.residential_commercial || [],
  imageUrl: result.imageUrl || '',
  name: result.name || '',
  sku: result.sku || '',
  brand: result.brand_name?.[0] || '',
  url: result.url || '',
  siblings: result.siblings || [],
  categories: result.categories || [],
  ssFacetColorFilter: result.ss_facet_color_filter || [],
  indoorOutdoorUse: result.indoor_outdoor_use || [],
  priceSign: result.price_sign || '',
  color: result.color || '',
  thumbnailImageUrl: result.thumbnailImageUrl || '',
  itemType: result.item_type || '',
  configurations: result.configurations || [],
  blurhash: result.blurhash || '',
  components: result.components || [],
  colorwayId: result._colorway_id || '',
  associatedFinishProducts: result.associated_finish_products || [],
  familyId: result.family_id || '',
  taxonomy: result.taxonomy || [],
  lightReflectanceValue: result.light_reflectance_value || '',
  originalMaterial: result,
  sampleNote: result.sample_note || '',
  isFinish: result.is_finish || false,
  isProduct: result.is_products || false,
  isPaint: result.is_paints || false,
  colorVariants: result.color_variants || { items: [], total_count: 0 },
  attachments: result.attachments || [],
  taxonomyL2: result.taxonomy_l2 || [],
  isDigital: result.is_digital || false,
  hasAttachments: result.has_attachments || false,
  visibility: result.visibility || '',
  isGpsCompliant: result.is_gps_compliant || false,
});

export const checkStockAvailability = async (
  results: ProductSearchMaterial[] = [],
) => {
  try {
    const entityIds = results.map((result) => result.entityId);
    const materialsStatus = await checkMultipleMaterialsAvailability(entityIds);

    if (materialsStatus == null) {
      return results;
    }

    const updatedResponses = results.map((product) => {
      if (Array.isArray(materialsStatus)) {
        for (const response of materialsStatus) {
          if (String(response.product_id) === String(product.entityId)) {
            return {
              ...product,
              isInRealTimeStock: Number(response.qty) > 0,
            };
          }
        }
      }
      if (product.isInRealTimeStock == null) {
        return {
          ...product,
          isInRealTimeStock: false,
        };
      }
      return product;
    });
    return updatedResponses;
  } catch (e) {
    console.log(e);
    handleSystemErrorReport(
      `Error while checking stock availability: ${e.message}`,
      'checkStockAvailability',
    );
    return results;
  }
};

export const checkMultipleMaterialsAvailability = async (
  materialsIds: string[],
) => {
  try {
    if (materialsIds?.length === 0) {
      return null;
    }

    const bodyFormData = new FormData();
    bodyFormData.append('items', materialsIds?.join(','));
    bodyFormData.append('origin', 'search');

    const response = await axios.post(
      '/catalog/product/inventory',
      bodyFormData,
    );
    const { data } = response;
    return data?.data;
  } catch (error) {
    console.error('Error while checking stock >>> ', error.message);
    handleSystemErrorReport(
      `Error while checking stock: ${error.message}`,
      'checkMultipleMaterialsAvailability',
    );
    return null;
  }
};

export function getUrlParameter(name: string, customUrl: string | null = null) {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(customUrl || window.location.search);
  return results === null
    ? ''
    : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

export function getUrlPageNumber() {
  const splitText = isSearchPage() ? 'search' : '#';
  const hrefChunks = window.location.href.split(splitText);
  const searchChunk = hrefChunks?.[1];
  if (searchChunk != null) {
    const pageChunk = searchChunk
      ?.split('/')
      ?.find((chunk) => chunk.includes('page'));
    return parseInt(pageChunk?.split(':')?.[1]?.replace('#', '') ?? '1');
  }
  return;
}

export function setUrlPageNumber(newNumber: number) {
  if (isHomePage() && !isBannersPage()) {
    return;
  }

  let newUrl = window.location.href;
  const pageRegEx = /(page:)(\d+)/;
  if (newUrl.match(pageRegEx)) {
    newUrl = newUrl.replace(pageRegEx, `page:${newNumber}`);
  } else {
    newUrl += `#/page:${newNumber}`;
  }

  if (window.location.href !== newUrl) {
    window.history.pushState({ path: newUrl }, '', newUrl);
  }
}

export function getUrlSortOption() {
  const hrefChunks = location.href.split('search');
  const searchChunk = hrefChunks?.[1];
  if (searchChunk != null) {
    const sortChunk = searchChunk
      ?.split('/')
      ?.find((chunk) => chunk.includes('sort'));
    const sortFilter = sortChunk?.split(':')?.[1];
    if (sortFilter != null) {
      return {
        field: sortFilter.split('=')?.[0],
        direction: sortFilter.split('=')?.[1]?.replace('#', ''),
        label: '',
      };
    }
  }
  return;
}

export function setUrlSortOption(
  option: SortOption | undefined,
  shouldRemove = false,
) {
  if (option == null) {
    return;
  }
  let newUrl = window.location.href;
  const sortRegEx = /sort:(.[^#]*)/;

  if (shouldRemove) {
    if (newUrl.match(sortRegEx)) {
      newUrl = newUrl.replace(sortRegEx, '');
      window.history.pushState({ path: newUrl }, '', newUrl);
      return;
    }
  }

  if (newUrl.match(sortRegEx)) {
    newUrl = newUrl.replace(
      sortRegEx,
      `sort:${option.field}=${option.direction}`,
    );
  } else {
    const optionalHash = newUrl.slice(-1) === '#' ? '/' : '#/';
    newUrl += `${optionalHash}sort:${option.field}=${option.direction}`;
  }

  window.history.pushState({ path: newUrl }, '', newUrl);
}

export function addCookiesFilters(appFilters: any, isBrandToggle: boolean) {
  try {
    const cookies = getCookies();
    for (const [key, value] of Object.entries(cookies)) {
      if (key === 'customer_member_type') {
        appFilters['bgfilter.member_type'] = value;
      }
      if (key === 'customer_manufacturer') {
        checkCustomerManufacturerFilter(appFilters, value, isBrandToggle);
      }
    }
  } catch (e) {
    handleSystemErrorReport(
      `Error while parsing cookies: ${e.message}`,
      'addCookiesFilters',
    );
    console.error('error parsing cookies', e);
  }
}

export function checkCustomerManufacturerFilter(
  appFilters: any,
  value: string,
  isBrandToggle: boolean,
) {
  try {
    const brandName = JSON.parse(decodeURIComponent(value))?.manufacturer_name;
    if (isBrandToggle) {
      appFilters['bgfilter.manufacturer'] = brandName;
      appFilters['bgfilter.brand_product'] = true;
    } else {
      delete appFilters['bgfilter.manufacturer'];
      appFilters['bgfilter.brand_product'] = false;
    }
  } catch (e) {
    delete appFilters['bgfilter.manufacturer'];
    appFilters['bgfilter.brand_product'] = false;
    handleSystemErrorReport(
      `Error while parsing cookies: ${e.message}`,
      'checkCustomerManufacturerFilter',
    );
  }
}
